import React, { useEffect, useState, useRef, useCallback } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTransition, animated } from "react-spring";

import {
  couponType,
  couponHistory,
} from "../../../../services/Api/Module/Customer";

import PageTitleMember from "../../../../layouts/inc/PageTitleMember";
import CardCouponHistory from "../../../../components/card/promotion/coupon/CardCouponHistory";
import MenuMember from "../../../../components/menu/MenuMember";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faArrowsRotate } from "@fortawesome/free-solid-svg-icons";

import img_icoTicketHide from "../../../../assets/img/ci/ico-coupon/ico-ticket-hide.svg";

const PULL_THRESHOLD = 60;
const MAX_PULL_DISTANCE = 80;

const MyCoupon = ({ articleItem }) => {
  let height = 0;

  const transitions = useTransition(
    articleItem.map((data) => ({
      ...data,
      y:
        (height += data.props.curHeight ? data.props.curHeight + 16 : 146) -
        (data.props.curHeight ? data.props.curHeight + 16 : 146),
    })),
    {
      key: (item) => item.key,
      from: { height: 0, opacity: 0, transform: "translateY(30px)" },
      enter: ({ y, height }) => ({
        y,
        height,
        opacity: 1,
        transform: "translateY(0)",
      }),
      leave: { height: 0, opacity: 0, transform: "translateY(30px)" },
      update: ({ data, y, height }) => ({ data, y, height }),
    }
  );

  return (
    <div
      className="item-group"
      style={{ position: "relative", height: height }}
    >
      {transitions((style, item, t, index) => (
        <animated.div
          className="item-coupon"
          style={{
            ...style,
            zIndex: articleItem.length - index,
            willChange: "transform, height, opacity",
          }}
        >
          {item}
        </animated.div>
      ))}
    </div>
  );
};

const CouponHistory = () => {
  const dispatch = useDispatch(); // ไว้อัพเดต state กลาง
  const stateProfile = useSelector((state) => state.profile);

  /* Handle goto page */
  const navigate = useNavigate();

  const handleGotoPage = (e) => {
    e.preventDefault();
    // console.log("click GotoPage", e.currentTarget.dataset.page);

    const page = e.currentTarget.dataset.page;
    navigate("/" + page);
  };
  /* End Handle goto page */

  /* Handle search */
  const [searchValue, setSearchValue] = useState("");

  const handleOnSearchChange = (e) => {
    // console.log("handleOnSearchChange >>", e.target.value);

    setSearchValue(e.target.value);
  };
  /* End Handle search */

  /* Handle type */
  const [typeValue, setTypeValue] = useState("");

  const handleOnTypeChange = (e) => {
    // console.log("handleOnTypeChange >>", e.target.value);

    setTypeValue(e.target.value);
  };
  /* End Handle type */

  /* Handle sort */
  const [sortValue, setSortValue] = useState("");

  const handleOnSortChange = (e) => {
    // console.log("handleOnSortChange >>", e.target.value);

    setSortValue(e.target.value);
  };
  /* End Handle sort */

  /* First event render */
  const [isLoading, setIsLoading] = useState(true);

  const [couponTypeList, setCouponTypeList] = useState([]);

  const [initItem, setInitItem] = useState([]);
  const [initHeight, setInitHeight] = useState([]);
  const [curHeight, setCurHeight] = useState([]);

  const [couponItem, setCouponItem] = useState([]);
  const [articleItem, setArticleItem] = useState([]);

  /* Pull to refresh */
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const touchStartY = useRef(0);
  const isAtTop = useRef(true);
  const pullDistanceRef = useRef(0);
  const isRefreshingRef = useRef(false);
  const listSectionRef = useRef(null);

  useEffect(() => {
    pullDistanceRef.current = pullDistance;
  }, [pullDistance]);

  useEffect(() => {
    isRefreshingRef.current = isRefreshing;
  }, [isRefreshing]);

  const handleRefresh = useCallback(async () => {
    if (isRefreshingRef.current) return;
    setIsRefreshing(true);
    setInitItem([]);
    setInitHeight([]);
    setCurHeight([]);
    setCouponItem([]);
    setArticleItem([]);

    const _rsDataCouponHistory = await getCouponHistory({
      custId: stateProfile.id,
    });
    if (
      _rsDataCouponHistory.status === true &&
      _rsDataCouponHistory.result &&
      _rsDataCouponHistory.result.length !== 0
    ) {
      const _coupon = _rsDataCouponHistory.result;
      setInitItem(_coupon);
      setInitHeight(_coupon.map(() => 0));
    }
    setIsRefreshing(false);
    setPullDistance(0);
  }, [stateProfile.id]);

  const handleTouchStart = useCallback((e) => {
    touchStartY.current = e.touches[0].clientY;
    isAtTop.current =
      window.scrollY === 0 || document.documentElement.scrollTop === 0;
  }, []);

  const handleTouchEnd = useCallback(() => {
    const currentPull = pullDistanceRef.current;
    if (currentPull >= PULL_THRESHOLD && !isRefreshingRef.current) {
      handleRefresh();
    } else {
      setPullDistance(0);
    }
  }, [handleRefresh]);

  /* touchmove ต้องใช้ addEventListener แบบ passive: false เพื่อให้ preventDefault() ทำงาน (แก้ error ใน console) */
  useEffect(() => {
    const el = listSectionRef.current;
    if (!el) return;

    const onTouchMove = (e) => {
      if (!isAtTop.current || isRefreshingRef.current) return;
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      if (scrollTop > 5) {
        isAtTop.current = false;
        setPullDistance(0);
        return;
      }
      const currentY = e.touches[0].clientY;
      const diff = currentY - touchStartY.current;
      if (diff > 0) {
        e.preventDefault();
        setPullDistance(Math.min(diff * 0.5, MAX_PULL_DISTANCE));
      }
    };

    el.addEventListener("touchmove", onTouchMove, { passive: false });
    return () => el.removeEventListener("touchmove", onTouchMove);
  }, []);
  /* End Pull to refresh */

  const getCouponType = async (payload) => {
    let _return = {
      status: false,
      result: null,
    };

    try {
      const { data } = await couponType({ params: payload });
      if (data.resultCode === 20200) {
        const resData = data.body;
        // console.log("getCustCoupon resData >>", resData);

        _return.status = true;
        _return.result = resData;
      }
    } catch (e) {
      console.log("ERR getCustCoupon >> ", e);
    }

    return _return;
  };

  const getCouponHistory = async (payload) => {
    let _return = {
      status: false,
      result: null,
    };

    try {
      const { data } = await couponHistory({ params: payload });
      if (data.resultCode === 20200) {
        const resData = data.body;
        // console.log("getCouponHistory resData >>", resData);

        _return.status = true;
        _return.result = resData;
      }
    } catch (e) {
      console.log("ERR getCouponHistory >> ", e);
    }

    return _return;
  };

  useEffect(() => {
    // console.log("useEffect !", stateProfile);

    if (
      stateProfile.id === 0 ||
      Object.getOwnPropertyNames(stateProfile.dataUser).length === 0
    ) {
      navigate("/");
    } else {
      getCouponType({}).then((_rsCouponType) => {
        // console.log("_rsCouponType >>>", _rsCouponType);
        setCouponTypeList(_rsCouponType.result);
      });
    }
  }, []);

  useEffect(() => {
    if (couponTypeList.length > 0) {
      getCouponHistory({
        custId: stateProfile.id,
      }).then((_rsDataCouponHsitory) => {
        // console.log("_rsDataCouponHsitory >>", _rsDataCouponHsitory);

        if (
          _rsDataCouponHsitory.status === true &&
          _rsDataCouponHsitory.result.length !== 0
        ) {
          const _length = _rsDataCouponHsitory.result.length;
          const _coupon = _rsDataCouponHsitory.result;
          // console.log(_coupon);

          for (let i = 0; i < _length; i++) {
            setInitItem((prevInitItem) => [...prevInitItem, _coupon[i]]);

            setInitHeight((prevCurHeight) => [...prevCurHeight, 0]);
          }
        }

        setIsLoading(false);
      });
    }
  }, [couponTypeList]);
  /* End First event render */

  /* Set new height on resize */
  let _tmpHeight = [];

  const setNewHeight = (index, height) => {
    // console.log(index, height, _tmpHeight);
    if (height !== 0) {
      _tmpHeight[index] = height;
      setCurHeight(_tmpHeight);
    }
  };
  /* End Set new height on resize */

  useEffect(() => {
    // console.log(initItem.length, initItem);

    if (initItem.length > 0) {
      for (let i = 0; i < initItem.length; i++) {
        setCouponItem((prevCouponItem) => [
          ...prevCouponItem,
          <CardCouponHistory
            key={i + 1}
            index={i}
            setNewHeight={setNewHeight}
            curHeight={initHeight[i]}
            dataCoupon={initItem[i]}
          />,
        ]);

        _tmpHeight[i] = initHeight[i];
      }
    }
  }, [initItem]);

  useEffect(() => {
    if (couponItem) {
      // console.log(couponItem.length, couponItem);

      let _tmpItem = couponItem;

      // console.log(sortValue);
      if (sortValue !== "") {
        switch (sortValue) {
          case "expiredat":
            _tmpItem = [...couponItem].sort((a, b) => {
              return a.props.dataCoupon.redeemExpiredAt >
                b.props.dataCoupon.redeemExpiredAt ||
                b.props.dataCoupon.redeemId > a.props.dataCoupon.redeemId
                ? 1
                : -1;
            });
            break;

          default:
            _tmpItem = [...couponItem].sort((a, b) => {
              return a.props.dataCoupon.redeemCreatedOn >
                b.props.dataCoupon.redeemCreatedOn ||
                a.props.dataCoupon.redeemId > b.props.dataCoupon.redeemId
                ? 1
                : -1;
            });
            break;
        }
      }

      // console.log(typeValue);
      if (typeValue !== "") {
        _tmpItem = _tmpItem.filter((item) => {
          if (item.props.dataCoupon.couponType === typeValue) {
            return item;
          }
        });
      } else {
        _tmpItem = _tmpItem.filter((item) => {
          return item;
        });
      }

      // console.log(searchValue);
      if (searchValue !== "") {
        _tmpItem = _tmpItem.filter((item) => {
          if (
            item.props.dataCoupon.name
              .toLowerCase()
              .includes(`${searchValue}`.toLowerCase())
          ) {
            return item;
          }
        });
      }

      setArticleItem(_tmpItem);
    }
  }, [couponItem, sortValue, typeValue, searchValue]);

  useEffect(() => {
    // console.log(curHeight.length, curHeight);

    if (curHeight.length > 0) {
      curHeight.map((val, idx) => {
        setCouponItem((prevCouponItem) =>
          prevCouponItem.with(
            idx,
            <CardCouponHistory
              key={couponItem[idx].key}
              index={idx}
              setNewHeight={setNewHeight}
              curHeight={val}
              dataCoupon={couponItem[idx].props.dataCoupon}
            />
          )
        );
      });
    }
  }, [curHeight]);

  useEffect(() => {
    // console.log(articleItem.length, articleItem);
  }, [isLoading, articleItem]);

  return (
    <>
      <Helmet>
        <title>Health Up</title>
        <meta name="title" content="Health Up" />
        <meta name="description" content="" />
        <meta name="keyword" content="" />
        <meta property="og:title" content="Health Up" />
        <meta property="og:description" content="" />
        <meta property="og:image" content="" />
        <meta property="og:url" content="" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Health Up" />
      </Helmet>

      <div id="pageCoupon-History">
        <main>
          <div className="wrapContent">
            <PageTitleMember title="โปรโมชั่น" />

            <section id="pSubmenuMain">
              <div className="selectMenu">
                <div
                  className="selectHeader active"
                  onClick={handleGotoPage}
                  data-page="coupon"
                >
                  <div className="coupon">
                    <p>แลกคูปอง</p>
                    <div className="underline"></div>
                  </div>
                </div>
                <div
                  className="selectHeader"
                  onClick={handleGotoPage}
                  data-page="promotion"
                >
                  <div className="promotion">
                    <p>โปรโมชั่น</p>
                    <div className="underline"></div>
                  </div>
                </div>
              </div>
              <div className="bgList">
                <div className="bSubmenu">
                  <a
                    className=""
                    href="#"
                    onClick={handleGotoPage}
                    data-page="coupon"
                  >
                    รายชื่อคูปอง
                  </a>
                  <a
                    className="active"
                    href="#"
                    onClick={handleGotoPage}
                    data-page="coupon/history"
                  >
                    ประวัติการใช้งาน
                  </a>
                </div>
                <section id="pSearchMain">
                  <div className="bSearch">
                    <input
                      type="text"
                      name="search"
                      id="inputSearch"
                      placeholder="ค้นหา"
                      onChange={handleOnSearchChange}
                    />
                  </div>
                  <select
                    className="bSelect"
                    name="selectType"
                    defaultValue={""}
                    onChange={handleOnTypeChange}
                  >
                    <option value={""}>ประเภท</option>
                    {couponTypeList.length > 0 &&
                      couponTypeList.map((item, index) => (
                        <option value={item.id} key={index + 1}>
                          {item.name}
                        </option>
                      ))}
                  </select>
                  <div className="bFilter">
                    <div className="select">
                      <select
                        name="selectSort"
                        defaultValue={""}
                        onChange={handleOnSortChange}
                      >
                        <option value={""} disabled></option>
                        <option value={"all"}>ทั้งหมด</option>
                        <option value={"expiredat"}>วันหมดอายุ</option>
                      </select>
                    </div>
                  </div>
                </section>
                <section
                  ref={listSectionRef}
                  id="pListCouponMain"
                  onTouchStart={handleTouchStart}
                  onTouchEnd={handleTouchEnd}
                  style={{ position: "relative" }}
                >
                  {/* Pull to refresh indicator */}
                  <div
                    className="pull-to-refresh-indicator"
                    style={{
                      position: "absolute",
                      left: 0,
                      right: 0,
                      top: 0,
                      height:
                        pullDistance || (isRefreshing ? PULL_THRESHOLD : 0),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: isRefreshing ? "height 0.2s" : "none",
                      zIndex: 10,
                      pointerEvents: "none",
                    }}
                  >
                    {(pullDistance > 0 || isRefreshing) && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          color: "#666",
                          fontSize: "14px",
                        }}
                      >
                        {isRefreshing ? (
                          <>
                            <FontAwesomeIcon icon={faSpinner} spin />
                            <span>กำลังโหลด...</span>
                          </>
                        ) : pullDistance >= PULL_THRESHOLD ? (
                          <>
                            <FontAwesomeIcon icon={faArrowsRotate} />
                            <span>ปล่อยเพื่อรีเฟรช</span>
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon
                              icon={faArrowsRotate}
                              style={{
                                transform: `rotate(${
                                  (pullDistance / PULL_THRESHOLD) * 180
                                }deg)`,
                              }}
                            />
                            <span>ดึงลงเพื่อรีเฟรช</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <div
                    className="bListCoupon"
                    style={{
                      transform:
                        pullDistance > 0
                          ? `translateY(${Math.min(
                              pullDistance,
                              MAX_PULL_DISTANCE
                            )}px)`
                          : undefined,
                      transition:
                        pullDistance === 0 ? "transform 0.2s" : "none",
                    }}
                  >
                    {isLoading ? (
                      <div className="bNoData">
                        <p>
                          <FontAwesomeIcon icon={faSpinner} spin /> Loading...
                        </p>
                      </div>
                    ) : (
                      <>
                        {articleItem.length === 0 ? (
                          <div className="bNoData">
                            <img
                              className="grayScale"
                              src={img_icoTicketHide}
                            />
                            <p>ยังไม่มีประวัติการใช้งานในขณะนี้</p>
                          </div>
                        ) : (
                          <div className="bGroup use">
                            <MyCoupon articleItem={articleItem} />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </section>
              </div>
            </section>

            <MenuMember
              pageActive="promotion"
              newSurvey={stateProfile.newSurvey}
              newSurveyPoint={stateProfile.newSurveyPoint}
            />
          </div>
        </main>
      </div>
    </>
  );
};

export default CouponHistory;
